import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import SearchInput from '@components/ui/SearchInput';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import EmptyState from '@components/ui/EmptyState';
import ErrorState from '@components/ui/ErrorState';
import './People.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Person {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  status: string;
  role_or_title: string | null;
  company_id: string | null;
  company_name: string | null;
  created_at: string;
  updated_at: string;
}

interface PeopleResponse {
  success: boolean;
  data: Person[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

// ─── Status badge variant mapping ────────────────────────────────────────────

const PERSON_STATUSES = [
  'Subscriber',
  'Prospect',
  'Attempted to contact',
  'Connected',
  'Qualified',
  'Opportunity',
  'Customer',
  'Unqualified',
] as const;

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Customer':
      return 'success';
    case 'Opportunity':
    case 'Qualified':
      return 'warning';
    case 'Unqualified':
      return 'danger';
    default:
      return 'default';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const People: React.FC = () => {
  const navigate = useNavigate();

  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [companies, setCompanies] = useState<Array<{ id: string; company_name: string }>>([]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch companies for filter dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch('/api/companies?page_size=100');
        if (!res.ok) return;
        const json = await res.json() as { success: boolean; data: Array<{ id: string; company_name: string }> };
        if (json.success) {
          setCompanies(json.data.sort((a, b) => a.company_name.localeCompare(b.company_name)));
        }
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  const fetchPeople = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (companyFilter) params.set('company_id', companyFilter);
      params.set('page_size', '50');

      const res = await fetch(`/api/people?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: PeopleResponse = await res.json();
      if (!json.success) throw new Error('API returned failure');
      setPeople(json.data);
      setTotal(json.pagination.total);
    } catch (err) {
      console.error('Failed to fetch people:', err);
      setError('Failed to load people. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, companyFilter]);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="people-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">People</h1>
          {!loading && !error && (
            <span className="page-count">{total} {total === 1 ? 'person' : 'people'}</span>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/people/new')}
        >
          + New person
        </Button>
      </div>

      {/* Filters */}
      <div className="people-filters">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, email or company…"
          className="people-search"
        />
        <select
          className="people-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {PERSON_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          className="people-status-filter"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="people-loading">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <ErrorState message={error} onRetry={fetchPeople} />
        </Card>
      ) : people.length === 0 ? (
        <Card>
          <EmptyState
            title={debouncedSearch || statusFilter || companyFilter ? 'No people match your filters' : 'No people yet'}
            message={
              debouncedSearch || statusFilter || companyFilter
                ? 'Try adjusting your search or filter.'
                : 'Add your first person to get started.'
            }
            action={
              !debouncedSearch && !statusFilter && !companyFilter ? (
                <Button variant="primary" size="sm" onClick={() => navigate('/people/new')}>
                  + New person
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="people-list">
          {people.map((person) => (
            <button
              key={person.id}
              className="person-row"
              onClick={() => navigate(`/people/${person.id}`)}
              type="button"
            >
              <div className="person-row-main">
                <span className="person-name">{person.full_name}</span>
                <Badge variant={statusVariant(person.status)}>{person.status}</Badge>
              </div>
              <div className="person-row-meta">
                {person.company_name && (
                  <span className="person-company">{person.company_name}</span>
                )}
                {person.email && (
                  <span className="person-email">{person.email}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default People;
